import React from 'react';
import {
  FileText,
  Settings,
  Activity,
  Shield,
  UserCheck,
  Radar,
  Plug,
  Clock,
  AlertTriangle,
  ExternalLink,
  Trash2,
  Edit2,
  CheckCircle
} from 'lucide-react';
import {
  EVIDENCE_TYPES,
  EVIDENCE_STATUS,
  getExpirationWarning
} from '../../config/cisaEvidenceModel';
import './EvidenceAttachmentCard.css';

const EVIDENCE_TYPE_ICONS = {
  POLICY_EXPORT: FileText,
  CONFIG_SNAPSHOT: Settings,
  LOG_COVERAGE: Activity,
  GOVERNANCE_ARTIFACT: Shield,
  MANUAL_ATTESTATION: UserCheck,
  AUTOMATED_SCAN: Radar,
  INTEGRATION_API: Plug
};

/**
 * EvidenceAttachmentCard - Displays a single evidence item with status and actions
 *
 * Shows:
 * - Evidence type with icon
 * - Source description
 * - Verification status
 * - Expiration warning
 * - Linked product/check info
 */
function EvidenceAttachmentCard({
  evidence,
  onEdit,
  onDelete,
  onView,
  compact = false
}) {
  const {
    id,
    type,
    source,
    status,
    verifiedAt,
    verifiedBy,
    expiresAt,
    productId,
    attachmentUrl,
    notes
  } = evidence;

  const typeConfig = EVIDENCE_TYPES[type] || EVIDENCE_TYPES.MANUAL_ATTESTATION;
  const Icon = EVIDENCE_TYPE_ICONS[type] || FileText;
  const expirationWarning = getExpirationWarning(expiresAt);
  const statusConfig = EVIDENCE_STATUS[status] || EVIDENCE_STATUS.MISSING;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysUntilExpiry = (expiresAt) => {
    if (!expiresAt) return null;
    const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const daysUntilExpiry = getDaysUntilExpiry(expiresAt);

  if (compact) {
    return (
      <div
        className={`eac-card eac-compact ${expirationWarning === 'expired' ? 'eac-expired' : ''}`}
        style={{ '--type-color': typeConfig.color }}
      >
        <div className="eac-icon-compact">
          <Icon size={14} />
        </div>
        <div className="eac-content-compact">
          <span className="eac-type-compact">{typeConfig.label}</span>
          <span className="eac-source-compact">{source}</span>
        </div>
        {status === 'VERIFIED' && (
          <CheckCircle size={14} className="eac-verified-icon" />
        )}
        {expirationWarning === 'expiring_soon' && (
          <AlertTriangle size={14} className="eac-expiring-icon" />
        )}
        {expirationWarning === 'expired' && (
          <AlertTriangle size={14} className="eac-expired-icon" />
        )}
      </div>
    );
  }

  return (
    <div
      className={`eac-card ${expirationWarning === 'expired' ? 'eac-expired' : ''}`}
      style={{ '--type-color': typeConfig.color }}
    >
      <div className="eac-header">
        <div className="eac-type-badge">
          <Icon size={16} />
          <span>{typeConfig.label}</span>
        </div>
        <div className={`eac-status-badge eac-status-${status.toLowerCase()}`}>
          {statusConfig.label}
        </div>
      </div>

      <div className="eac-source">{source}</div>

      {notes && <div className="eac-notes">{notes}</div>}

      <div className="eac-meta">
        {verifiedAt && (
          <div className="eac-meta-item">
            <Clock size={12} />
            <span>Verified {formatDate(verifiedAt)}</span>
            {verifiedBy && <span className="eac-by">by {verifiedBy}</span>}
          </div>
        )}

        {productId && (
          <div className="eac-meta-item eac-product-link">
            <span>Linked to {productId}</span>
          </div>
        )}
      </div>

      {/* Expiration Warning */}
      {expiresAt && (
        <div
          className={`eac-expiration ${
            expirationWarning === 'expired'
              ? 'eac-expiration-expired'
              : expirationWarning === 'expiring_soon'
              ? 'eac-expiration-soon'
              : ''
          }`}
        >
          {expirationWarning === 'expired' ? (
            <>
              <AlertTriangle size={12} />
              <span>Expired {Math.abs(daysUntilExpiry)} days ago</span>
            </>
          ) : expirationWarning === 'expiring_soon' ? (
            <>
              <AlertTriangle size={12} />
              <span>Expires in {daysUntilExpiry} days</span>
            </>
          ) : (
            <>
              <Clock size={12} />
              <span>Expires {formatDate(expiresAt)}</span>
            </>
          )}
        </div>
      )}

      <div className="eac-actions">
        {attachmentUrl && (
          <button
            className="eac-action-btn"
            onClick={() => onView?.(evidence)}
            title="View attachment"
          >
            <ExternalLink size={14} />
          </button>
        )}
        <button
          className="eac-action-btn"
          onClick={() => onEdit?.(evidence)}
          title="Edit evidence"
        >
          <Edit2 size={14} />
        </button>
        <button
          className="eac-action-btn eac-delete-btn"
          onClick={() => onDelete?.(evidence)}
          title="Delete evidence"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

export default EvidenceAttachmentCard;
